/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import classNames from 'classnames';
import reactUpdate from 'immutability-helper';
import React from 'react';
import _ from 'underscore';

import * as CrisprConstants from 'constants/CRISPRConstants';
import makeCancelable from 'lib/makeCancelable';
import CRISPRRegionDAO from 'molbio/crispr/CRISPRRegionDAO';
import * as sequtil from 'molbio/SeqUtil';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Clipboard from 'ui-library/Clipboard';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import * as GenomeUtil from 'util/GenomeUtil';

const {MAX_OFF_TARGET_GUIDE_LENGTH, OFF_TARGET_SCORE_TYPE, URLS} = CrisprConstants;

const ASC = 'asc';
const DESC = 'desc';
const SORT_KEY_SCORE = 'score';
const SORT_KEY_GENE = 'gene';
const SORT_KEY_PAM = 'pam';
const SORT_KEY_LOCUS = 'locus';

interface Props {
  analysis: any;
  guide: any;
  onRequestClose: (...args: Array<any>) => any;
  region: any;
}

interface State {
  sortKey: string;
  sortOrder: string;
  exons: Array<any> | null;
}

export default class OffTargetModal extends React.Component<Props, State> {
  matchExonsPromise: any;
  state: State = {
    sortKey: SORT_KEY_SCORE,
    sortOrder: DESC,
    exons: null,
  };

  componentDidMount(): void {
    this.matchExonsPromise = makeCancelable(
      Promise.resolve(CRISPRRegionDAO.matchExons(this.props.region.id, this.props.guide.sites))
    );
    this.matchExonsPromise.promise
      .then(({data}: any) => {
        this.setState({exons: data});
      })
      .catch((response: any) => {
        if (response.isCanceled) {
          return;
        }
        notifications.notifyError(response);
      });
  }

  componentWillUnmount(): void {
    this.matchExonsPromise.cancel();
  }

  getDisplayPosition = (site: any): number => sequtil.modelStartToDisplay(site.position, 1);

  getStrandSymbol = (site: any): '+' | '-' => {
    if (site.strand === 1) {
      return '+';
    } else {
      return '-';
    }
  };

  getGeneName = (site: any): string | null => {
    if (site.exon == null) {
      return null;
    }
    return GenomeUtil.geneToName({name: site.exon.geneName, ensemblId: site.exon.geneEnsemblId});
  };

  getLocus = (site: any): string =>
    `${site.chromosomeName}:${this.getStrandSymbol(site)}${this.getDisplayPosition(site)}`;

  getMismatches = (site: any): {[key: number]: boolean} => {
    const siteBasesUpper = site.bases.toUpperCase();
    const guideBasesUpper = this.props.guide.bases.toUpperCase();
    const mismatches: {[key: number]: boolean} = {};
    for (let i = 0, end = site.bases.length, asc = 0 <= end; asc ? i < end : i > end; asc ? i++ : i--) {
      if (siteBasesUpper[i] !== guideBasesUpper[i]) {
        mismatches[i] = true;
      }
    }
    return mismatches;
  };

  getSortableChromosomeName = (site: any): string => {
    if (Number.isNaN(site.chromosomeName)) {
      return site.chromosomeName;
    }
    return `0000${site.chromosomeName}`.slice(-4);
  }; // pad with 0s

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getSortablePAM = (sortablePAMIndex: any, site: any) => site.pam.slice(sortablePAMIndex);

  isOnTarget = (site: any): boolean => {
    if (this.props.region.genomeRegionChromosome == null) {
      return false;
    }
    if (this.props.region.genomeRegionChromosome.name !== site.chromosomeName) {
      return false;
    }
    return (
      this.props.region.genomeRegionStart <= site.position &&
      site.position < this.props.region.genomeRegionEnd
    );
  };

  sitesToTSV = (sortedSites: any): string => {
    const tsvHeaderRows = ['Sequence\tPAM\tScore\tGene\tChromosome\tStrand\tPosition\tMismatches\tOn-target'];
    const tsvRows = sortedSites.map((site: any) =>
      [
        site.bases,
        site.pam,
        site.score,
        site.exon != null ? site.exon.geneEnsemblId : undefined,
        site.chromosomeName,
        site.strand,
        this.getDisplayPosition(site),
        // eslint-disable-next-line no-restricted-syntax
        _.size(this.getMismatches(site)),
        Boolean(this.isOnTarget(site)),
      ].join('\t')
    );
    return tsvHeaderRows.concat(tsvRows).join('\n');
  };

  sortSites = (sortKey: string): void => {
    if (this.state.sortKey === sortKey) {
      this.setState({sortOrder: this.state.sortOrder === ASC ? DESC : ASC});
    } else {
      this.setState({sortKey, sortOrder: sortKey === SORT_KEY_PAM ? DESC : ASC});
    }
  };

  renderSiteSequence = (site: any): Array<JSX.Element> => {
    const mismatches = this.getMismatches(site);
    return Array.from(site.bases).map((base: string, i) => (
      <span key={i} className={classNames({mismatch: mismatches[i]})}>
        {base}
      </span>
    ));
  };

  renderColumnHeader = (sortKey: string, header: JSX.Element | string): JSX.Element => (
    <th className="u-no-select">
      <a
        className="u-no-text-decoration"
        onClick={() => {
          this.sortSites(sortKey);
        }}
      >
        {header}
        {this.state.sortKey === sortKey ? (
          <BenchlingIcon icon={this.state.sortOrder === ASC ? 'chevron-up' : 'chevron-down'} />
        ) : undefined}
      </a>
    </th>
  );

  renderOffTargetScoreText = (): JSX.Element => {
    switch (this.props.analysis.offTargetScoreType) {
      case OFF_TARGET_SCORE_TYPE.MIT:
        return (
          <p>
            {'Our scoring calculation follows the method from '}
            <a href={URLS.HSU_OFF_TARGET_PAPER} rel="noopener noreferrer" target="_blank">
              Hsu et al., 2013
            </a>
            .
          </p>
        );
      case OFF_TARGET_SCORE_TYPE.CFD:
        return (
          <p>
            {'Our scoring calculation uses the Cutting Frequency Determination (CFD) score from '}
            <a href={URLS.CFD_SCORE_PAPER} rel="noopener noreferrer" target="_blank">
              Doench, Fusi et al. (2016).
            </a>
            {' Scores above 20 indicate a high probability of off-target effects at that site.'}
          </p>
        );
      default:
        throw new Error('Unexpected off-target score type.');
    }
  };

  render(): JSX.Element {
    let {sites} = this.props.guide;
    if (this.state.exons != null) {
      // eslint-disable-next-line no-restricted-syntax
      sites = _.map(_.zip(sites, this.state.exons), (...args) => {
        const [site, exon] = Array.from(args[0]);
        // @ts-expect-error: Fixme (Typescript 4.8 upgrade)
        return reactUpdate(site, {$merge: {exon}});
      });
    }

    let sortablePAMIndex = 0;
    while (this.props.analysis.pam[sortablePAMIndex] === 'N') {
      sortablePAMIndex++;
    }
    let chainedSortedSites = _.chain(sites);
    chainedSortedSites = (() => {
      switch (this.state.sortKey) {
        case SORT_KEY_PAM:
          return chainedSortedSites
            .sortBy('score')
            .sortBy((site) => this.getSortablePAM(sortablePAMIndex, site));
        case SORT_KEY_SCORE:
          return chainedSortedSites.sortBy('score');
        case SORT_KEY_GENE:
          return chainedSortedSites.sortBy(this.getGeneName);
        case SORT_KEY_LOCUS:
          return chainedSortedSites.sortBy('position').sortBy(this.getSortableChromosomeName);
        default:
          throw new Error('Unexpected sort key');
      }
    })();
    const sortedSites = chainedSortedSites.value();
    if (this.state.sortOrder === DESC) {
      sortedSites.reverse();
    }
    return (
      <Modal
        enableRedesign={true}
        large={true}
        title="Off-target sites"
        onRequestClose={this.props.onRequestClose}
        wrapInForm={false}
      >
        <div className="text-right u-small-spacing-bottom">
          <Clipboard
            value={this.sitesToTSV(sortedSites)}
            notifyCopied={() => {
              notifications.success(
                `Off-targets have been copied in tab-delimited format, which can be pasted \
                 into Excel.`,
                {duration: 6, id: 'off-targets-copied'}
              );
            }}
          >
            <Button buttonStyle={ButtonStyle.TERTIARY_GRAY} size={ButtonSize.SMALL} text="Copy TSV" />
          </Clipboard>
        </div>
        <div className="offtarget-table-container">
          <table className="table table-condensed table-hover offtarget-table">
            <thead>
              <tr>
                <th>Sequence</th>
                {this.renderColumnHeader(SORT_KEY_PAM, 'PAM')}
                {this.renderColumnHeader(SORT_KEY_SCORE, 'Score')}
                {this.renderColumnHeader(
                  SORT_KEY_GENE,
                  <span>
                    Gene
                    {this.state.exons == null ? <span className="faded"> (Loading...)</span> : undefined}
                  </span>
                )}
                {this.renderColumnHeader(SORT_KEY_LOCUS, 'Cut locus')}
              </tr>
            </thead>
            <tbody className="selectable">
              {(() => {
                const result = [];
                for (const site of Array.from(sortedSites)) {
                  result.push(
                    <tr
                      key={`site-${site.position}-${site.strand}`}
                      className={classNames({faded: this.isOnTarget(site)})}
                    >
                      <td>{this.renderSiteSequence(site)}</td>
                      <td>{site.pam}</td>
                      <td>{site.score.toFixed(1)}</td>
                      <td>{this.getGeneName(site)}</td>
                      <td>{this.getLocus(site)}</td>
                    </tr>
                  );
                }
                return result;
              })()}
            </tbody>
          </table>
        </div>
        {!this.props.analysis.isLeftPam && this.renderOffTargetScoreText()}
        {(() => {
          switch (this.props.analysis.pam) {
            case 'NGG':
            case 'NAG':
              return (
                <p>
                  {`For SpCas9, we include off-target matches for NAG and NGG since both have measurable
                    efficiency (`}
                  <a href={URLS.NGG_PAM} rel="noopener noreferrer" target="_blank">
                    {URLS.NGG_PAM}
                  </a>
                  ).
                </p>
              );
            case 'NNGRRT':
              return (
                <p>
                  {`For the NNGRRT PAM, we include off-target matches for NNGRR instead, which has been shown
                    to have measurable efficiency across both (`}
                  <a href={URLS.NNGRRT_PAM} rel="noopener noreferrer" target="_blank">
                    {URLS.NNGRRT_PAM}
                  </a>
                  ).
                </p>
              );
            default:
              return null;
          }
        })()}
        {this.props.guide.bases.length > MAX_OFF_TARGET_GUIDE_LENGTH ? (
          <p>
            <BenchlingIcon className="u-warning-color" icon="warning" />
            {` The off-target analysis only takes into account the ${MAX_OFF_TARGET_GUIDE_LENGTH} bp closest
              to PAM.`}
          </p>
        ) : undefined}
      </Modal>
    );
  }
}
